<template>
  <div class="dashboard">
    <MobileHeader @toggle-sidebar="toggleSidebar" />
    
    <Sidebar 
      :isOpen="sidebarOpen" 
      :isMobile="isMobile" 
      @close="closeSidebar"
    />
    
    <main class="main-content" :class="{ 'main-content-mobile': isMobile }">
      <header class="header">
        <h1>Управление на категории</h1>
        <div class="header-controls">
          <!--
          <div class="language-filter">
            <label for="languageFilter">Филтър по език:</label>
            <select
              id="languageFilter"
              v-model="selectedLanguage"
              class="form-control language-select"
            >
              <option value="">Всички езици</option>
              <option value="EN">Английски (EN)</option>
              <option value="BG">Български (BG)</option>
            </select>
          </div>
          -->
          <button @click="showCreateModal = true" class="btn btn-primary">
            <i class="fas fa-plus"></i>
            Добави категория
          </button>
        </div>
      </header>
      
      <div class="content">
        <div class="card">
          <div class="card-header">
            <h3>Чат категории</h3>
            <p class="subtitle">Управление на чат категориите и подредбата им</p>
          </div>
          
          <div v-if="loading" class="loading">
            <i class="fas fa-spinner fa-spin"></i>
            Зареждане на категории...
          </div>
          
          <div v-else-if="filteredCategoriesList.length === 0 && selectedLanguage" class="no-data">
            <i class="fas fa-language"></i>
            <p>Няма намерени категории за избрания език ({{ selectedLanguage }})</p>
            <button @click="selectedLanguage = ''" class="btn btn-secondary">
              Покажи всички категории
            </button>
          </div>
          
          <div v-else-if="categoriesList.length === 0" class="no-data">
            <i class="fas fa-tags"></i>
            <p>Няма намерени категории</p>
            <button @click="showCreateModal = true" class="btn btn-primary">
              Създай първа категория
            </button>
          </div>
          
          <div v-else class="categories-list">
            <div
              v-for="(category, index) in filteredCategoriesList"
              :key="category.id"
              class="category-item"
              :class="{ 'category-dragging': dragging === category.id }"
              draggable="true"
              @dragstart="onDragStart($event, category, index)"
              @dragover="onDragOver($event)"
              @drop="onDrop($event, index)"
              @dragend="onDragEnd"
            >
              <div class="top-row">
                <div class="category-drag-handle">
                  <i class="fas fa-grip-vertical"></i>
                </div>
                
                <div class="category-info">
                  <div class="category-header">
                    <h4>{{ category.name }}</h4>
                    <div class="category-meta">
                      <span class="message-count">
                        <i class="fas fa-comments"></i>
                        {{ getMessageCount(category) }} съобщения
                      </span>
                      <span class="order-badge">Позиция: {{ getLanguageSpecificOrder(category) }}</span>
                    </div>
                  </div>

                  <div class="category-details">
                    <span class="created-date">
                      Създадена: {{ formatDate(category.createdAt) }}
                    </span>
                    <span v-if="category.lastMessage" class="last-message">
                      Последно съобщение: {{ formatDate(category.lastMessage.timestamp) }}
                    </span>
                    <span class="church-info">
                      <i class="fas fa-church"></i>
                      {{ getChurchDisplay(category) }}
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="category-actions">
                <button
                  @click="editCategory(category)"
                  class="btn btn-sm btn-primary"
                  title="Редактирай категория"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  @click="deleteCategory(category)"
                  class="btn btn-sm btn-danger"
                  title="Изтрий категория"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    
    <!-- Create/Edit Category Modal -->
    <div v-if="showCreateModal || editingCategory" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ editingCategory ? 'Редактирай категория' : 'Създай нова категория' }}</h3>
          <button @click="closeModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="saveCategory">
            <div class="form-group">
              <label class="form-label">Име на категория *</label>
              <input
                type="text"
                v-model="categoryForm.name"
                class="form-control"
                placeholder="Въведете име на категория"
                required
              />
            </div>
            
            <!-- <div class="form-group">
              <label class="form-label">Език *</label>
              <input
                type="hidden"
                v-model="categoryForm.language"
                value="BG"
              />
              Temporarily removed language selection for BG-only version
              <select
                v-model="categoryForm.language"
                class="form-control"
                :disabled="editingCategory"
                required
              >
                <option value="EN">Английски (EN)</option>
                <option value="BG">Български (BG)</option>
              </select>
              <small v-if="editingCategory" class="form-help">
                Езикът не може да бъде променян при редактиране
              </small>
             
            </div> -->
            
            <div class="form-group">
              <label class="form-label">Църква</label>
              <select
                v-model="selectedChurchId"
                class="form-control"
              >
                <option value="">Всички църкви</option>
                <option 
                  v-for="church in churchesList" 
                  :key="church.id" 
                  :value="church.id"
                >
                  {{ church.name.bg }}
                </option>
              </select>
              <small class="form-help">
                Изберете църква за която тази категория ще бъде видима. "Всички църкви" означава, че категорията ще бъде видима за всички.
              </small>
            </div>
            
            <div class="form-group">
              <label class="form-label">Позиция</label>
              <input
                type="number"
                v-model.number="categoryForm.order"
                class="form-control"
                min="1"
                :max="getMaxOrderForLanguage(categoryForm.language)"
                placeholder="Позиция за показване"
              />
              <small class="form-help">
                Позиция в рамките на {{ categoryForm.language === 'BG' ? 'българските' : 'английските' }} категории
              </small>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeModal" class="btn btn-secondary">
                Отказ
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                {{ saving ? 'Запазване...' : (editingCategory ? 'Обнови' : 'Създай') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="deletingCategory" class="modal-overlay" @click="cancelDelete">
      <div class="modal modal-small" @click.stop>
        <div class="modal-header">
          <h3>Изтрий категория</h3>
          <button @click="cancelDelete" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <p>Сигурни ли сте, че искате да изтриете категория "<strong>{{ deletingCategory.name }}</strong>"?</p>
          <p class="warning-text">Това действие не може да бъде отменено и ще изтрие всички съобщения в тази категория.</p>
          
          <div class="form-actions">
            <button @click="cancelDelete" class="btn btn-secondary">
              Отказ
            </button>
            <button @click="confirmDelete" class="btn btn-danger" :disabled="deleting">
              <i class="fas fa-spinner fa-spin" v-if="deleting"></i>
              {{ deleting ? 'Изтриване...' : 'Изтрий категория' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { signOut } from 'firebase/auth'
import { auth, database } from '../firebase'
import { ref as dbRef, onValue, push, update, remove } from 'firebase/database'
import Sidebar from './Sidebar.vue'
import MobileHeader from './MobileHeader.vue'
import moment from 'moment'

export default {
  name: 'CategoriesPage',
  components: {
    Sidebar,
    MobileHeader
  },
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      categories: {},
      churches: {},
      selectedLanguage: 'BG', // Only BG supported in this version
      showCreateModal: false,
      editingCategory: null,
      deletingCategory: null,
      dragging: null,
      draggedOverIndex: null,
      sidebarOpen: false,
      isMobile: false,
      categoryForm: {
        name: '',
        language: 'BG', // Only BG supported in this version
        order: 1,
        churchIds: [] // Array to support multiple churches in the future
      }
    }
  },
  computed: {
    categoriesList() {
      return Object.entries(this.categories)
        .map(([id, category]) => ({ id, ...category }))
        .filter(category => category.language === 'BG') // Only show BG categories in this version
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    filteredCategoriesList() {
      if (!this.selectedLanguage) {
        return this.categoriesList
      }
      return this.categoriesList.filter(category => 
        category.language === this.selectedLanguage
      )
    },
    churchesList() {
      return Object.entries(this.churches)
        .map(([id, church]) => ({ id, ...church }))
        .sort((a, b) => (a.name?.bg || '').localeCompare(b.name?.bg || ''))
    },
    selectedChurchId: {
      get() {
        // Return single church ID for current UI, but internally stored as array
        return this.categoryForm.churchIds && this.categoryForm.churchIds.length > 0 
          ? this.categoryForm.churchIds[0] 
          : ''
      },
      set(value) {
        // Store as array to support future multiple selection
        this.categoryForm.churchIds = value ? [value] : []
      }
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    closeSidebar() {
      this.sidebarOpen = false
    },
    async logout() {
      try {
        await signOut(auth)
        this.$router.push('/login')
      } catch (error) {
        console.error('Logout error:', error)
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      return moment(timestamp).format('MMM DD, YYYY HH:mm')
    },
    getMessageCount(category) {
      if (!category.messages) return 0
      return Object.keys(category.messages).length
    },
    getLanguageSpecificOrder(category) {
      // Return the actual stored order value, which should be language-specific
      return category.order || 1
    },
    getMaxOrderForLanguage(language) {
      const sameLanguageCategories = this.categoriesList.filter(cat => 
        cat.language === language
      )
      return this.editingCategory ? sameLanguageCategories.length : sameLanguageCategories.length + 1
    },
    getChurchDisplay(category) {
      if (!category.churchIds || category.churchIds.length === 0) {
        return 'Всички църкви'
      }
      
      const churchNames = category.churchIds.map(churchId => {
        const church = this.churches[churchId]
        return church ? church.name.bg : 'Неизвестна църква'
      })
      
      return churchNames.join(', ')
    },
    editCategory(category) {
      this.editingCategory = category
      this.categoryForm = {
        name: category.name,
        language: category.language || 'BG',
        order: category.order || 1,
        churchIds: category.churchIds || []
      }
    },
    deleteCategory(category) {
      this.deletingCategory = category
    },
    closeModal() {
      this.showCreateModal = false
      this.editingCategory = null
      this.categoryForm = {
        name: '',
        language: 'BG',
        order: 1,
        churchIds: []
      }
    },
    cancelDelete() {
      this.deletingCategory = null
    },
    async saveCategory() {
      this.saving = true
      
      try {
        const categoryData = {
          name: this.categoryForm.name,
          language: this.categoryForm.language,
          churchIds: this.categoryForm.churchIds || [], // Store as array for future multiple selection
          updatedAt: Date.now()
        }

        if (this.editingCategory) {
          // When editing, only handle order changes (language cannot change)
          const currentOrder = this.editingCategory.order || 1
          const newOrder = this.categoryForm.order || 1
          
          if (currentOrder !== newOrder) {
            // Order changed - need to reorder categories within the same language
            await this.reorderCategoriesAfterEdit(this.editingCategory, this.categoryForm.language, newOrder)
          }
          
          categoryData.order = newOrder
          
          // Update existing category
          await update(dbRef(database, `categories/${this.editingCategory.id}`), categoryData)
        } else {
          // For new categories, assign the next order number for the selected language
          const maxOrder = this.getMaxOrderForLanguage(this.categoryForm.language)
          const finalOrder = this.categoryForm.order && this.categoryForm.order <= maxOrder 
            ? this.categoryForm.order 
            : maxOrder
          
          if (this.categoryForm.order && this.categoryForm.order < maxOrder) {
            // Need to shift other categories in this language
            await this.shiftCategoriesForNewOrder(this.categoryForm.language, finalOrder)
          }
          
          categoryData.order = finalOrder
          categoryData.createdAt = Date.now()
          
          await push(dbRef(database, 'categories'), categoryData)
        }

        this.closeModal()
      } catch (error) {
        console.error('Error saving category:', error)
      } finally {
        this.saving = false
      }
    },
    async confirmDelete() {
      this.deleting = true
      
      try {
        const deletedCategory = this.deletingCategory
        await remove(dbRef(database, `categories/${deletedCategory.id}`))
        
        // Reorder remaining categories in the same language to fill gaps
        await this.reorderCategoriesAfterDelete(deletedCategory.language, deletedCategory.order)
        
        this.cancelDelete()
      } catch (error) {
        console.error('Error deleting category:', error)
      } finally {
        this.deleting = false
      }
    },
    
    async reorderCategoriesAfterEdit(editedCategory, language, newOrder) {
      const updates = {}
      
      // Get all categories in the same language excluding the edited one
      const sameLanguageCategories = this.categoriesList
        .filter(cat => cat.language === language && cat.id !== editedCategory.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      
      // Create new order array with the edited category inserted at new position
      const newOrderArray = [...sameLanguageCategories]
      newOrderArray.splice(Math.max(0, newOrder - 1), 0, { ...editedCategory, order: newOrder })
      
      // Assign sequential orders starting from 1
      newOrderArray.forEach((cat, index) => {
        if (cat.id !== editedCategory.id) {
          updates[`categories/${cat.id}/order`] = index + 1
        }
      })
      
      if (Object.keys(updates).length > 0) {
        await update(dbRef(database), updates)
      }
    },
    
    async shiftCategoriesForNewOrder(language, insertOrder) {
      const sameLanguageCategories = this.categoriesList
        .filter(cat => cat.language === language)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      
      const updates = {}
      
      sameLanguageCategories.forEach(cat => {
        if ((cat.order || 1) >= insertOrder) {
          updates[`categories/${cat.id}/order`] = cat.order + 1
        }
      })
      
      if (Object.keys(updates).length > 0) {
        await update(dbRef(database), updates)
      }
    },
    
    async reorderCategoriesAfterDelete(language, deletedOrder) {
      const sameLanguageCategories = this.categoriesList
        .filter(cat => cat.language === language)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
      
      const updates = {}
      
      // Shift down all categories that had higher order than the deleted one
      sameLanguageCategories.forEach(cat => {
        if ((cat.order || 1) > deletedOrder) {
          updates[`categories/${cat.id}/order`] = cat.order - 1
        }
      })
      
      if (Object.keys(updates).length > 0) {
        await update(dbRef(database), updates)
      }
    },
    // Drag and drop functionality
    onDragStart(event, category) {
      this.dragging = category.id
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target)
    },
    onDragOver(event) {
      if (event.preventDefault) {
        event.preventDefault()
      }
      event.dataTransfer.dropEffect = 'move'
      return false
    },
    async onDrop(event, targetIndex) {
      if (event.stopPropagation) {
        event.stopPropagation()
      }

      const draggedCategory = this.filteredCategoriesList.find(cat => cat.id === this.dragging)
      if (!draggedCategory) return

      let draggedIndex = this.filteredCategoriesList.findIndex(cat => cat.id === this.dragging)
      
      if (draggedIndex !== targetIndex) {
        // When we have a language filter, work within that filtered list
        // When no filter, work within the same language as the dragged category
        let categoriesInScope
        
        if (this.selectedLanguage) {
          // Working within filtered language
          categoriesInScope = this.filteredCategoriesList
        } else {
          // No filter - only reorder within the same language
          categoriesInScope = this.categoriesList.filter(cat => 
            cat.language === draggedCategory.language
          )
          
          // Recalculate target index within the same language categories
          const draggedIndexInLanguage = categoriesInScope.findIndex(cat => cat.id === this.dragging)
          
          // Find what target category this corresponds to in the filtered view
          const targetCategory = this.filteredCategoriesList[targetIndex]
          const targetIndexInLanguage = categoriesInScope.findIndex(cat => cat.id === targetCategory.id)
          
          if (targetIndexInLanguage === -1) {
            // Target is not in the same language, ignore this drop
            this.dragging = null
            this.draggedOverIndex = null
            return
          }
          
          targetIndex = targetIndexInLanguage
          draggedIndex = draggedIndexInLanguage
        }
        
        // Create a new ordered list
        const newList = [...categoriesInScope]
        
        // Remove dragged item and insert at new position
        newList.splice(draggedIndex, 1)
        newList.splice(targetIndex, 0, draggedCategory)
        
        // Update orders starting from 1
        const updates = {}
        newList.forEach((category, index) => {
          updates[`categories/${category.id}/order`] = index + 1
        })

        try {
          await update(dbRef(database), updates)
        } catch (error) {
          console.error('Error updating category order:', error)
        }
      }

      this.dragging = null
      this.draggedOverIndex = null
    },
    onDragEnd() {
      this.dragging = null
      this.draggedOverIndex = null
    },
    loadCategories() {
      const categoriesRef = dbRef(database, 'categories')
      onValue(categoriesRef, (snapshot) => {
        this.categories = snapshot.val() || {}
        this.loading = false
      })
    },
    loadChurches() {
      const churchesRef = dbRef(database, 'churches')
      onValue(churchesRef, (snapshot) => {
        this.churches = snapshot.val() || {}
      })
    }
  },
  mounted() {
    this.loadCategories()
    this.loadChurches()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  }
}
</script>

<style scoped>
.dashboard {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

.sidebar {
  width: 260px;
  background: #1D687B;
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid rgba(121, 218, 242, 0.2);
  text-align: center;
}

.logo-placeholder {
  margin-bottom: 16px;
}

.logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  background: #F8F8F8;
  padding: 8px;
}

.sidebar-header h2 {
  font-size: 20px;
  font-weight: 700;
  color: #F8F8F8;
}

.sidebar-menu {
  flex: 1;
  list-style: none;
  padding: 20px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #F8F8F8;
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.menu-item:hover {
  background-color: rgba(121, 218, 242, 0.2);
  color: #79daf2;
}

.menu-item.active {
  background-color: #79daf2;
  color: #1D687B;
  border-left-color: #F8F8F8;
}

.menu-item i {
  width: 16px;
  text-align: center;
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(121, 218, 242, 0.2);
}

.logout-btn {
  width: 100%;
  justify-content: center;
}

.main-content {
  flex: 1;
  margin-left: 260px;
  background: #F8F8F8;
}

.main-content-mobile {
  margin-left: 0;
}

.header {
  background: white;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.language-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.language-filter label {
  color: #333333;
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
}

.language-select {
  min-width: 150px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #333333;
}

.language-select:focus {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

.header h1 {
  color: #333333;
  font-size: 28px;
  font-size: min(max(20px, 4vw), 28px);
  font-weight: 700;
}

.content {
  padding: 32px 16px;
}

.card {
  padding: 24px;
}

.card-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h3 {
  color: #333333;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 4px;
}

.subtitle {
  color: #666666;
  font-size: 14px;
}

.categories-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.top-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: all 0.3s ease;
  cursor: move;
}

.category-item:hover {
  border-color: #1D687B;
  box-shadow: 0 4px 6px -1px rgba(29, 104, 123, 0.1);
}

.category-dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

.category-drag-handle {
  display: flex;
  align-items: center;
  color: #6b7280;
  cursor: grab;
  padding: 8px;
}

.category-drag-handle:active {
  cursor: grabbing;
}

.category-info {
  flex: 1;
}

.category-header {
  display: flex;
  justify-content: between;
  align-items: start;
  margin-bottom: 8px;
  gap: 16px;
}

.category-header h4 {
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.category-meta {
  display: flex;
  gap: 12px;
  align-items: center;
}

.message-count {
  color: #6b7280;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.order-badge {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.language-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.language-badge.language-EN {
  background: #dbeafe;
  color: #1e40af;
}

.language-badge.language-BG {
  background: #fef3c7;
  color: #d97706;
}

.category-details {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #9ca3af;
  flex-wrap: wrap;
}

.church-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.church-info i {
  color: #1D687B;
}

.category-actions {
  display: flex;
  gap: 12px;
}

.btn-sm {
  padding: 10px 14px;
  font-size: 14px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 20px;
}

.modal-small {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  color: #1f2937;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: color 0.3s ease;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.close-btn:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.modal-body {
  padding: 24px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.warning-text {
  color: #dc2626;
  font-size: 14px;
  margin-top: 8px;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.no-data i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.no-data p {
  margin-bottom: 10px;
}

.form-help {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.form-group {
  margin-bottom: 20px;
}

.form-control:disabled {
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 768px) {
  .language-filter {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 14px;
  }

  .category-drag-handle {
    font-size: 24px;
  }

  .card {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
    background-color: transparent;
  }

  .card-header {
    display: none;
  }
  
  .main-content {
    margin-left: 0;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 16px 20px;
  }
  
  .header-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .language-filter {
    justify-content: space-between;
  }

  .top-row { 
    flex-direction: row;
  }
  
  .category-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .category-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .category-meta {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  
  .category-actions {
    gap: 8px;
    align-self: stretch;
  }
  
  .modal {
    margin: 10px;
    max-width: calc(100vw - 20px);
  }
  
  .form-group {
    margin-bottom: 16px;
  }
}
</style>
